import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Ticket, Plus, Copy, Trash2, ArrowLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { vouchersAPI } from '../utils/api';

const PLAN_OPTIONS = [
  { value: 'growth', label: 'Professional ($19/mo equivalent)' },
  { value: 'pro',    label: 'Business ($49/mo equivalent)' },
];

const DURATION_OPTIONS = [
  { value: '1',  label: '1 month' },
  { value: '3',  label: '3 months' },
  { value: '6',  label: '6 months' },
  { value: '12', label: '12 months (1 year)' },
];

export const AdminVouchersPage = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [revoking, setRevoking] = useState(null);

  // Form state
  const [code, setCode] = useState('');
  const [planType, setPlanType] = useState('growth');
  const [durationMonths, setDurationMonths] = useState('1');
  const [maxUses, setMaxUses] = useState('1');
  const [appliesTo, setAppliesTo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersAPI.adminList();
      setVouchers(res.data || []);
    } catch (e) {
      toast.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  const resetForm = () => {
    setCode('');
    setPlanType('growth');
    setDurationMonths('1');
    setMaxUses('1');
    setAppliesTo('');
    setNote('');
  };

  const handleCreate = async () => {
    setBusy(true);
    try {
      const payload = {
        plan_type: planType,
        duration_months: parseInt(durationMonths, 10),
        max_uses: maxUses ? parseInt(maxUses, 10) : 1,
      };
      if (code.trim()) payload.code = code.trim().toUpperCase();
      if (appliesTo.trim()) payload.applies_to_user_email = appliesTo.trim().toLowerCase();
      if (note.trim()) payload.note = note.trim();
      await vouchersAPI.adminCreate(payload);
      toast.success('Voucher created');
      setShowCreate(false);
      resetForm();
      fetchVouchers();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to create voucher';
      toast.error(typeof detail === 'string' ? detail : 'Failed to create voucher');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (voucherId) => {
    setBusy(true);
    try {
      await vouchersAPI.adminRevoke(voucherId);
      toast.success('Voucher revoked. Existing redeemers keep their plan until natural expiry.');
      setRevoking(null);
      fetchVouchers();
    } catch (e) {
      toast.error('Failed to revoke voucher');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = (c) => {
    navigator.clipboard.writeText(c);
    toast.success(`Code "${c}" copied`);
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin">
              <Button variant="ghost" size="sm" data-testid="back-to-admin">
                <ArrowLeft className="w-4 h-4 mr-1" /> Admin
              </Button>
            </Link>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-900 flex items-center gap-2" data-testid="vouchers-page-title">
                <Ticket className="w-5 h-5 text-violet-600" /> Vouchers
              </h1>
              <p className="text-sm text-slate-500">Generate, distribute and revoke voucher codes</p>
            </div>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            data-testid="create-voucher-btn"
            className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New voucher
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Card>
          <CardHeader>
            <CardTitle>All vouchers ({vouchers.length})</CardTitle>
            <CardDescription>Revoked vouchers keep existing redeemers active until natural expiry.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
            ) : vouchers.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center" data-testid="vouchers-empty-state">
                No vouchers yet. Click <span className="font-semibold">New voucher</span> to create one.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Uses</TableHead>
                      <TableHead>Bound to</TableHead>
                      <TableHead>Code expires</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vouchers.map((v) => {
                      const used = v.current_uses || 0;
                      const cap = v.max_uses;
                      const usedDisplay = cap == null ? `${used} (unlimited)` : `${used} / ${cap}`;
                      const fullyUsed = cap != null && used >= cap;
                      return (
                        <TableRow key={v.id} data-testid={`voucher-row-${v.code}`}>
                          <TableCell>
                            <button
                              onClick={() => copyCode(v.code)}
                              className="font-mono font-bold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1"
                              data-testid={`copy-voucher-${v.code}`}
                            >
                              {v.code}
                              <Copy className="w-3 h-3 opacity-50" />
                            </button>
                          </TableCell>
                          <TableCell>{v.plan_type === 'growth' ? 'Professional' : 'Business'}</TableCell>
                          <TableCell>{v.duration_months} mo</TableCell>
                          <TableCell>{usedDisplay}</TableCell>
                          <TableCell className="text-xs text-slate-500">{v.applies_to_user_email || '— anyone —'}</TableCell>
                          <TableCell>{fmtDate(v.expires_at)}</TableCell>
                          <TableCell>
                            {v.is_revoked ? (
                              <Badge variant="outline" className="text-rose-700 border-rose-200 bg-rose-50">Revoked</Badge>
                            ) : fullyUsed ? (
                              <Badge variant="outline" className="text-slate-600 border-slate-200 bg-slate-50">Fully used</Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {!v.is_revoked && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRevoking(v)}
                                data-testid={`revoke-voucher-${v.code}`}
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) resetForm(); }}>
        <DialogContent data-testid="create-voucher-modal">
          <DialogHeader>
            <DialogTitle>New voucher</DialogTitle>
            <DialogDescription>Generate a code to grant a paid plan for a fixed duration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="voucher-code">Code (optional — auto-generated if left blank)</Label>
              <Input
                id="voucher-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="FORMLIO2026"
                className="font-mono uppercase"
                data-testid="voucher-code-input"
                maxLength={32}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Plan</Label>
                <Select value={planType} onValueChange={setPlanType}>
                  <SelectTrigger data-testid="voucher-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLAN_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration</Label>
                <Select value={durationMonths} onValueChange={setDurationMonths}>
                  <SelectTrigger data-testid="voucher-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="voucher-max-uses">Max redemptions</Label>
              <Input
                id="voucher-max-uses"
                type="number"
                min={1}
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                data-testid="voucher-max-uses-input"
              />
              <p className="text-xs text-slate-500 mt-1">Use 1 for a personal one-shot voucher.</p>
            </div>
            <div>
              <Label htmlFor="voucher-applies-to">Bind to user email (optional)</Label>
              <Input
                id="voucher-applies-to"
                type="email"
                value={appliesTo}
                onChange={(e) => setAppliesTo(e.target.value)}
                placeholder="friend@example.com"
                data-testid="voucher-applies-to-input"
              />
              <p className="text-xs text-slate-500 mt-1">Only this email can redeem. Leave blank for open redemption.</p>
            </div>
            <div>
              <Label htmlFor="voucher-note">Note (internal only)</Label>
              <Input
                id="voucher-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Gift for Alice"
                data-testid="voucher-note-input"
                maxLength={120}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)} disabled={busy} data-testid="voucher-create-cancel">Cancel</Button>
            <Button onClick={handleCreate} disabled={busy} data-testid="voucher-create-submit"
              className="bg-violet-600 hover:bg-violet-700 text-white">
              {busy ? 'Creating…' : 'Create voucher'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirm */}
      <Dialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <DialogContent data-testid="revoke-voucher-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" /> Revoke voucher?
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono font-bold">{revoking?.code}</span> will no longer be redeemable.
              Users who already redeemed it keep their granted plan until natural expiry.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRevoking(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={() => handleRevoke(revoking?.id)}
              disabled={busy}
              data-testid="revoke-voucher-confirm"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {busy ? 'Revoking…' : 'Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVouchersPage;
